// components/Sidebar.tsx
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';
import {
  sidebarContainerSx,
  sidebarHeaderSx,
  sidebarLogoWrapperSx,
  sidebarNavItemSx,
  sidebarNavItemActiveSx,
  sidebarNavBadgeSx,
} from '../../theme/recipes/navigation';
import {
  Dashboard as DashboardIcon,
  MedicalServices as MedicalIcon,
  Event as EventIcon,
  EventAvailable as EventAvailableIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  LocalHospital as HospitalIcon,
  MergeType as MergeTypeIcon,
  Policy as PolicyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useSubscription } from '../../hooks/useSubscription';
import MediCareLogo from '../common/MediCareLogo';
import { RoleCode } from '../../types/enums/roleCodes';
import { isDoctorRoleCode } from '../../utils/roleHelpers';
import UpcomingAppointmentsSlider from './UpcomingAppointmentsSlider';
import { isAdminContext } from '../../utils/access';

interface SidebarItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  readOnly?: boolean;
  comingSoon?: boolean;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSpecialtyActive } = useAuthStore();
  const { toggleSidebar } = useUIStore();

  // Menú principal para administradores
  const adminMainMenuItems: SidebarItem[] = [
    {
      path: '/',
      label: 'Dashboard',
      icon: <DashboardIcon />,
    },
    {
      path: '/institutions',
      label: 'Instituciones',
      icon: <BusinessIcon />,
    },
    {
      path: '/users',
      label: 'Usuarios',
      icon: <PeopleIcon />,
    },
  ];

  // Menú secundario para administradores
  const adminSettingsMenuItems: SidebarItem[] = [
    {
      path: '/specialties',
      label: 'Especialidades',
      icon: <MedicalIcon />,
    },
    {
      path: '/roles',
      label: 'Roles',
      icon: <SecurityIcon />,
    },
    {
      path: '/patients/reconciliation',
      label: 'Reconciliacion',
      icon: <MergeTypeIcon />,
    },
    {
      path: '/audit',
      label: 'Auditoria',
      icon: <PolicyIcon />,
    },
  ];

  // Menú para doctores
  const doctorMainMenuItems: SidebarItem[] = [
    {
      path: '/',
      label: 'Dashboard',
      icon: <DashboardIcon />,
    },
    {
      path: '/patients',
      label: 'Pacientes',
      icon: <PeopleIcon />,
    },
    {
      path: '/medical-records',
      label: 'Historias Clínicas',
      icon: <MedicalIcon />,
    },
    {
      path: '/appointments',
      label: 'Turnos',
      icon: <EventIcon />,
    },
  ];

  const doctorSettingsMenuItems: SidebarItem[] = [
    {
      path: '/schedule-settings',
      label: 'Agenda',
      icon: <EventAvailableIcon />,
    },
    {
      path: '/appointment-types',
      label: 'Tipos de Turno',
      icon: <PlaylistAddCheckIcon />,
    },
    {
      path: '/consents',
      label: 'Consentimientos',
      icon: <DescriptionIcon />,
      readOnly: true,
      comingSoon: true,
    },
    {
      path: '/patients/reconciliation',
      label: 'Reconciliacion',
      icon: <MergeTypeIcon />,
      readOnly: true,
      comingSoon: true,
    },
  ];

  const isDoctorSettingsRoute = React.useMemo(
    () => doctorSettingsMenuItems.some((item) => location.pathname === item.path),
    [location.pathname]
  );

  const isAdminSettingsRoute = React.useMemo(
    () => adminSettingsMenuItems.some((item) => location.pathname === item.path),
    [location.pathname]
  );

  const isAdminUser = isAdminContext(user);
  const settingsGroupItems = isAdminUser ? adminSettingsMenuItems : doctorSettingsMenuItems;
  const isSettingsRoute = isAdminUser ? isAdminSettingsRoute : isDoctorSettingsRoute;

  const [settingsOpen, setSettingsOpen] = React.useState(isSettingsRoute);

  React.useEffect(() => {
    if (isSettingsRoute) {
      setSettingsOpen(true);
    }
  }, [isSettingsRoute]);

  // Suscripción deshabilitada temporalmente: acceso completo para todos
  const hasActiveSubscription = true;

  // Seleccionar menú según el rol del usuario y estado de suscripción
  const menuItems = React.useMemo(() => {
    if (isAdminUser) {
      return adminMainMenuItems;
    }

    // Si no tiene suscripción activa, solo mostrar suscripción
    if (!hasActiveSubscription) {
      return [];
    }

    return doctorMainMenuItems;
  }, [isAdminUser, hasActiveSubscription]);

  const bottomMenuItems: SidebarItem[] = React.useMemo(() => {
    const items: SidebarItem[] = [];

    // Si no tiene suscripción activa, solo mostrar suscripción
    if (!hasActiveSubscription && !isAdminContext(user)) {
      return [
        {
          path: '/subscription',
          label: 'Suscripción',
          icon: <PaymentIcon />,
          readOnly: true,
          comingSoon: true,
        },
      ];
    }

    // Menú normal para usuarios con suscripción activa o admin
    items.push(
      {
        path: '/profile',
        label: 'Perfil',
        icon: <PersonIcon />,
      },
      {
        path: '/settings',
        label: 'Configuración',
        icon: <SettingsIcon />,
      }
    );

    // Solo mostrar suscripción para doctores (no admin)
    if (!isAdminContext(user)) {
      items.push({
        path: '/subscription',
        label: 'Suscripción',
        icon: <PaymentIcon />,
        readOnly: true,
        comingSoon: true,
      });
    }

    return items;
  }, [hasActiveSubscription, user]);

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 900) {
      toggleSidebar();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarItem: React.FC<{ item: SidebarItem }> = ({ item }) => {
    const isActive = location.pathname === item.path;

    return (
      <motion.div
        whileHover={{ x: 4 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.1 }}
      >
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => {
              if (!item.readOnly) {
                handleNavigation(item.path);
              }
            }}
            disabled={item.readOnly}
            sx={isActive ? sidebarNavItemActiveSx : sidebarNavItemSx}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.comingSoon ? 'Proximamente' : undefined}
              primaryTypographyProps={{
                fontWeight: isActive ? 600 : 500,
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                sx: {
                  color: 'warning.main',
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  mt: 0.25,
                  lineHeight: 1.2,
                },
              }}
            />
            {item.badge && (
              <Box sx={sidebarNavBadgeSx}>
                {item.badge}
              </Box>
            )}
          </ListItemButton>
        </ListItem>
      </motion.div>
    );
  };

  return (
    <Box sx={sidebarContainerSx}>
      {/* Header */}
      <Box sx={sidebarHeaderSx}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={sidebarLogoWrapperSx}>
            <MediCareLogo variant="full" width={200} height={56} />
          </Box>
        </motion.div>
      </Box>

      {/* Próximos Turnos Slider y separación - Solo para doctores con suscripción activa y especialidad activa */}
      {isDoctorRoleCode(user?.role?.code) &&
        hasActiveSubscription &&
        isSpecialtyActive && (
          <>
            <UpcomingAppointmentsSlider />
            <Divider sx={{ my: 2 }} />
          </>
        )}

      {/* Navigation Menu */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 2,
        }}
      >
        <List>
          {menuItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <SidebarItem item={item} />
            </motion.div>
          ))}

          {(isAdminUser || hasActiveSubscription) && settingsGroupItems.length > 0 && (
            <>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: menuItems.length * 0.1 }}
              >
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => setSettingsOpen((prev) => !prev)}
                    sx={isSettingsRoute ? sidebarNavItemActiveSx : sidebarNavItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Configuración"
                      primaryTypographyProps={{
                        fontWeight: isSettingsRoute ? 600 : 500,
                      }}
                    />
                    {settingsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
              </motion.div>

              <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: 1 }}>
                  {settingsGroupItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.2,
                        delay: (menuItems.length + index + 1) * 0.05,
                      }}
                    >
                      <SidebarItem item={item} />
                    </motion.div>
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>

        {/* Solo mostrar divider si hay menú principal */}
        {menuItems.length > 0 && <Divider sx={{ my: 2 }} />}

        <List>
          {bottomMenuItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.3,
                delay: (index + menuItems.length) * 0.1,
              }}
            >
              <SidebarItem item={item} />
            </motion.div>
          ))}
        </List>
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <motion.div
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.1 }}
        >
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Cerrar Sesión"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Sidebar;
